import { type FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'react-bootstrap';
import { BsX } from 'react-icons/bs';
import cx from 'clsx';

import {
	type EocCheckinChatbot,
	type KeyConcept as KeyConceptType
} from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';

import ButtonTooltip from '@editor2/components/ButtonTooltip';
import { keyConceptRow } from './styles';

interface Props {
	element: EocCheckinChatbot;
	concept: KeyConceptType;
	index: number;
	courseId: number;
}

const KeyConcept: FC<Props> = ({ element, concept, index, courseId }) => {
	const commonPayload = { elementId: element.family_id, courseId };

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const conceptMapper = useCallback(
		({ concept, description }: KeyConceptType) => ({ concept, description }),
		[]
	);

	const [conceptState, handleLocalChange] = useDebounceProtectedState(concept, conceptMapper);

	// Rebuilds the key_concepts array with this row replaced by an updated,
	// normalized object (other rows keep their original authored shape).
	const replaceConcept = (updated: KeyConceptType) => [
		...element.key_concepts.slice(0, index),
		updated,
		...element.key_concepts.slice(index + 1)
	];

	const handleConceptChange = (value: string) => {
		const updated = { concept: value, description: conceptState.description };
		handleLocalChange(updated);
		debouncedDispatch(updateElement({ ...commonPayload, key_concepts: replaceConcept(updated) }));
	};

	const handleDescriptionChange = (value: string) => {
		const updated = { concept: conceptState.concept, description: value };
		handleLocalChange(updated);
		debouncedDispatch(updateElement({ ...commonPayload, key_concepts: replaceConcept(updated) }));
	};

	const handleRemove = () => {
		// eslint-disable-next-line no-alert
		if (!window.confirm('Remove this key concept?')) return;

		dispatch(
			updateElement({
				...commonPayload,
				key_concepts: [
					...element.key_concepts.slice(0, index),
					...element.key_concepts.slice(index + 1)
				]
			})
		);
	};

	return (
		<div className={cx(keyConceptRow, 'keyConcept')}>
			<div className="conceptHeader">
				<span className="conceptIndex">Key concept {index + 1}</span>
				<ButtonTooltip message="Remove key concept">
					<BsX
						className="removeConcept"
						role="button"
						aria-label={`Remove key concept ${index + 1}`}
						onClick={handleRemove}
					/>
				</ButtonTooltip>
			</div>

			<Form.Group className="mb-0">
				<div className="fieldLabel">Concept</div>
				<Form.Control
					as="textarea"
					rows={2}
					placeholder="What should the student be able to demonstrate?"
					value={conceptState.concept}
					onChange={(e) => handleConceptChange(e.target.value)}
				/>
			</Form.Group>

			<Form.Group className="descriptionField">
				<div className="fieldLabel">Description (optional)</div>
				<Form.Control
					as="textarea"
					rows={2}
					placeholder="What does success look like for this concept?"
					value={conceptState.description}
					onChange={(e) => handleDescriptionChange(e.target.value)}
				/>
			</Form.Group>
		</div>
	);
};

export default KeyConcept;
